import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { anlagenAPI, wartungenAPI, qrCodeAPI } from '../services/api';
import { ArrowLeft, QrCode as QrCodeIcon, Plus, Download, Calendar, User, Wrench, Edit } from 'lucide-react';
import Navbar from '../components/Navbar';

const AnlageDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [anlage, setAnlage] = useState(null);
  const [wartungen, setWartungen] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [anlageData, wartungenData] = await Promise.all([
        anlagenAPI.getById(id),
        wartungenAPI.getByAnlage(id)
      ]);
      setAnlage(anlageData);
      setWartungen(wartungenData);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    if (anlage) {
      const link = document.createElement('a');
      link.href = qrCodeAPI.generate(anlage.qr_code);
      link.download = `qr-code-${anlage.seriennummer}.png`;
      link.click();
    }
  };

  const downloadPDF = async (wartungId) => {
    try {
      const blob = await wartungenAPI.exportPDF(wartungId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wartungsprotokoll-${wartungId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim Exportieren des PDFs');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Laden...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!anlage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-lg text-red-600">Anlage nicht gefunden</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück zur Übersicht
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-6">
            <div className="flex-1">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4">
                {anlage.anlagentyp}
              </span>
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="anlage-title">
                {anlage.hersteller} - {anlage.modell}
              </h1>
              <p className="text-gray-600">Seriennummer: {anlage.seriennummer}</p>
            </div>
            
            <div className="flex flex-col items-center gap-3">
              <div className="bg-white p-3 border-4 border-blue-600 rounded-lg">
                <img 
                  src={qrCodeAPI.generate(anlage.qr_code)} 
                  alt="QR Code"
                  className="w-32 h-32"
                  data-testid="qr-code-image"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/anlage/${id}/bearbeiten`)}
                  className="flex items-center gap-2 text-sm bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
                  data-testid="edit-button"
                >
                  <Edit className="w-4 h-4" />
                  Bearbeiten
                </button>
                <button
                  onClick={downloadQR}
                  className="flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
                  data-testid="download-qr-button"
                >
                  <Download className="w-4 h-4" />
                  QR-Code
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 pt-8 border-t border-gray-200">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Technische Daten</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Leistung</dt>
                  <dd className="text-base font-medium text-gray-900">{anlage.leistung}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Baujahr</dt>
                  <dd className="text-base font-medium text-gray-900">{anlage.baujahr}</dd>
                </div>
                {anlage.kaeltemittel && (
                  <div>
                    <dt className="text-sm text-gray-500">Kältemittel</dt>
                    <dd className="text-base font-medium text-gray-900">{anlage.kaeltemittel}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Standort</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm text-gray-500">Adresse</dt>
                  <dd className="text-base font-medium text-gray-900">{anlage.standort_adresse}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Gebäude</dt>
                  <dd className="text-base font-medium text-gray-900">{anlage.standort_gebaeude}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Raum</dt>
                  <dd className="text-base font-medium text-gray-900">{anlage.standort_raum}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Wartungshistorie</h2>
            <div className="flex gap-3">
              <Link
                to={`/wartung/vorlage/${id}`}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
                data-testid="new-wartung-vorlage-button"
              >
                <Plus className="w-5 h-5" />
                Konforme Wartung (Vorschriften)
              </Link>
              <Link
                to={`/wartung/neu/${id}`}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition"
                data-testid="new-wartung-button"
              >
                <Plus className="w-5 h-5" />
                Schnellerfassung
              </Link>
            </div>
          </div>

          {wartungen.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Noch keine Wartungen</h3>
              <p className="text-gray-500 mb-6">Dokumentieren Sie die erste Wartung für diese Anlage</p>
              <div className="flex gap-3 justify-center">
                <Link
                  to={`/wartung/vorlage/${id}`}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  Konforme Wartung
                </Link>
                <Link
                  to={`/wartung/neu/${id}`}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  Schnellerfassung
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="wartungen-list">
              {wartungen.map((wartung) => (
                <div
                  key={wartung.id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition"
                  data-testid={`wartung-card-${wartung.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span className="font-medium">{formatDate(wartung.wartungsdatum)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{wartung.techniker_name}</span>
                        </div>
                      </div>
                      {wartung.betriebsstunden && (
                        <p className="text-sm text-gray-500">Betriebsstunden: {wartung.betriebsstunden}h</p>
                      )}
                    </div>
                    <button
                      onClick={() => downloadPDF(wartung.id)}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition text-sm"
                      data-testid={`download-pdf-${wartung.id}`}
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">Durchgeführte Arbeiten:</h4>
                      <p className="text-gray-600 text-sm">{wartung.durchgefuehrte_arbeiten}</p>
                    </div>

                    {wartung.messwerte && Object.keys(wartung.messwerte).length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Messwerte:</h4>
                        <div className="flex flex-wrap gap-3">
                          {Object.entries(wartung.messwerte).map(([key, value]) => (
                            <span key={key} className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                              {key}: {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {wartung.austauschteile && wartung.austauschteile.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Austauschteile:</h4>
                        <ul className="list-disc list-inside text-gray-600 text-sm">
                          {wartung.austauschteile.map((teil, idx) => (
                            <li key={idx}>{teil}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {wartung.maengel && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-1">Mängel/Reparaturen:</h4>
                        <p className="text-gray-600 text-sm">{wartung.maengel}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnlageDetails;